// Portal-only safety check. This does not change API deletion behaviour or cascade records.
import React, { useEffect, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import { Button, Popup } from 'semantic-ui-react';
import { fetchItemsWS } from '../../../../webservices/fetchWS';

export const sectionHasChildren = (id, sections, questions) => Boolean(id) && (
  sections.some(row => row.status !== 'isDeleted' && row.parentSectionId === id) ||
  questions.some(row => row.status !== 'isDeleted' && row.sectionId === id)
);

export default function SectionDeleteButton({ section = {}, questionnaire, disabled, onClick, size }) {
  const store = useStore();
  const sections = useSelector(state => state.sections);
  const questions = useSelector(state => state.questions);
  const [check, setCheck] = useState(null);
  const id = section.id;
  const tenant = questionnaire.tenantId;
  const questionnaireId = questionnaire.id;
  useEffect(() => {
    let cancelled = false;
    if (!id || disabled) return () => { cancelled = true; };
    // Questions are loaded lazily elsewhere in the builder. Check both lists
    // before enabling delete, and refresh after local child edits/deletions.
    const params = { PK: `${tenant}%23QNAIRE%23${questionnaireId}` };
    Promise.all(['SECTION%23', 'QUESTION%23'].map(SK =>
      fetchItemsWS('get-dynamodb', { ...params, SK }, store.getState),
    )).then(([sectionRows, questionRows]) => {
      if (!Array.isArray(sectionRows.Items) || !Array.isArray(questionRows.Items)) throw new Error('Incomplete section check');
      if (!cancelled) setCheck({ id, tenant, questionnaireId, sections, questions,
        hasChildren: sectionHasChildren(id, sectionRows.Items, questionRows.Items) });
    }).catch(() => {
      if (!cancelled) setCheck({ id, tenant, questionnaireId, sections, questions, failed: true });
    });
    return () => { cancelled = true; };
  }, [id, tenant, questionnaireId, disabled, sections, questions, store]);
  const current = check && check.id === id && check.tenant === tenant && check.questionnaireId === questionnaireId && check.sections === sections && check.questions === questions;
  const blocked = disabled || !id || !current || check.failed || check.hasChildren;
  const explanation = !id ? 'Select a section first.' : disabled ? 'This section cannot be deleted.'
    : !current ? 'Checking questions and subsections…'
      : check.failed ? 'Unable to check this section. Reload the page before deleting.'
        : check.hasChildren ? 'Delete this section’s questions and subsections first.' : 'Delete section';
  return (
    <Popup content={explanation} on={['hover', 'focus']} trigger={
      <span tabIndex={blocked ? 0 : undefined} aria-label={blocked ? explanation : undefined} style={{ display: 'inline-flex' }}>
        <Button type="button" aria-label="Delete section" disabled={Boolean(blocked)} className="danger" icon="trash" size={size}
          onClick={event => { if (!blocked) onClick(event); }} />
      </span>
    } />
  );
}
